import type {Context} from '../types';
import type {JSONNode} from '@packages/shared/types/JSONNode';

function isImageRefFragment(fragment: any): boolean {
  if (!fragment || typeof fragment !== 'object') {
    return false;
  }
  return fragment.type === 'Set'
    ? fragment.innerType === 'ImageRef'
    : fragment.type === 'ImageRef';
}

function isFileRefFragment(fragment: any): boolean {
  if (!fragment || typeof fragment !== 'object') {
    return false;
  }

  return fragment.type === 'FileRef';
}

function isVideoFragment(fragment: any): boolean {
  if (!fragment || typeof fragment !== 'object') {
    return false;
  }

  return fragment.type === 'Video';
}

function isTargetedFragment(fragment: any) {
  return (
    isImageRefFragment(fragment) ||
    isFileRefFragment(fragment) ||
    isVideoFragment(fragment)
  );
}

function isLegacyBinding(binding: any): boolean {
  if (Array.isArray(binding)) {
    const lastIndex = binding.length - 1;
    // We are only iterating until the second to the last item because
    // the last fragment never has anything left to drop.
    for (let index = 0; index < lastIndex; index++) {
      const fragment = binding[index];
      if (isTargetedFragment(fragment)) {
        return true;
      }
    }
  }
  return false;
}

function isStyleBindingMapWithLegacyBinding(styleBindingMap: any): boolean {
  if (typeof styleBindingMap === 'object' && !Array.isArray(styleBindingMap)) {
    for (const styleBindingKey in styleBindingMap) {
      const binding = styleBindingMap[styleBindingKey];
      if (isLegacyBinding(binding)) {
        return true;
      }
    }
  }
  return false;
}

function isBindingMapWithLegacyBinding(bindingMap: any): boolean {
  if (typeof bindingMap === 'object' && !Array.isArray(bindingMap)) {
    for (const bindingKey in bindingMap) {
      switch (bindingKey) {
        case 'token': {
          // Token bindings are something very different so
          // we don't want to touch those in this patch.
          break;
        }
        case 'style': {
          const styleBindingMap = bindingMap[bindingKey];
          if (isStyleBindingMapWithLegacyBinding(styleBindingMap)) {
            return true;
          }
          break;
        }
        default: {
          const binding = bindingMap[bindingKey];
          if (isLegacyBinding(binding)) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

function patchLegacyBinding<T extends any[]>(binding: T): T {
  if (Array.isArray(binding)) {
    const lastIndex = binding.length - 1;
    for (let index = 0; index < lastIndex; index++) {
      const fragment = binding[index];
      if (isTargetedFragment(fragment)) {
        return binding.slice(0, index + 1) as T;
      }
    }
  }
  return binding;
}

export default (context: Context) => {
  const {
    node: {jsonNode},
  } = context;
  const {data = {}} = jsonNode as JSONNode;

  // Nodes can only have either node.data.dyn.bind
  // or node.data.search.bind but never both.
  const bindingMap: any = data?.dyn?.bind || data?.search?.bind;

  return {
    needed:
      typeof bindingMap === 'object' &&
      isBindingMapWithLegacyBinding(bindingMap),
    logData: {
      msg: 'Dynamo binding with a legacy sub path fragment',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply() {
      // Even though `needed` checks that `bindingMap` is an object there is no
      // guarantee for when this function is called, so we have the if statement
      // here for type safety. In reality, whenever the migration system calls
      // this `apply` the `if` statement here is truthy.
      if (
        typeof bindingMap === 'object' &&
        isBindingMapWithLegacyBinding(bindingMap)
      ) {
        for (const bindingKey in bindingMap) {
          switch (bindingKey) {
            case 'token': {
              break;
            }
            case 'style': {
              const styleBindingMap = bindingMap[bindingKey];
              if (
                typeof styleBindingMap === 'object' &&
                !Array.isArray(styleBindingMap)
              ) {
                for (const styleBindingKey in styleBindingMap) {
                  const binding = styleBindingMap[styleBindingKey];
                  if (isLegacyBinding(binding)) {
                    (styleBindingMap as Record<any, any>)[styleBindingKey] =
                      patchLegacyBinding(binding);
                  }
                }
              }
              break;
            }
            default: {
              const binding = bindingMap[bindingKey];
              if (isLegacyBinding(binding)) {
                (bindingMap as Record<any, any>)[bindingKey] =
                  patchLegacyBinding(binding);
              }
            }
          }
        }
      }
    },
  };
};
